import {useEffect, useRef} from 'react';
import {t} from '../../../common/getTranslations';
import {stopPropagationEvent} from '../../../common/shared/utils/stopPropagationEvent.function';
import {SEARCH_INPUT_MAX_LENGTH} from '../../../domain/search/constant';
import {useGetSearchKeywordQueryData} from '../../../domain/search/hook/useGetSearchKeywordQueryData';
import {selectAutoCompleteCustomProps} from '../../../domain/search/state/selector';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../header/constants';
import {aggregateAndSendLog} from '../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {NavibarInputField} from '../NavibarInputField/NavibarInputField';
import {MobileSearchHeaderProps} from './MobileSearchHeaderProps.interface';

export function MobileSearchHeader({
  onClickBack,
  searchPlaceholder,
  searchText,
  onChangeSearchText,
  shouldShowCancelButton,
  onClickCancelButton,
  onClickClearSearchText,
  onSubmit,
  onClickSearchInput,
  onFocusInput,
  onBlurInput,
  isMobile,
}: MobileSearchHeaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const {searchInfiniteData} = useGetSearchKeywordQueryData();
  const autoCompleteCustomProps =
    selectAutoCompleteCustomProps(searchInfiniteData);

  useEffect(() => {
    const inputEl = inputRef.current;
    if (inputEl && onFocusInput) {
      inputEl.addEventListener('focus', onFocusInput);
    }
    return () => {
      if (inputEl && onFocusInput) {
        inputEl.removeEventListener('focus', onFocusInput);
      }
    };
  }, [onFocusInput]);

  useEffect(() => {
    const inputEl = inputRef.current;
    if (inputEl && onBlurInput) {
      inputEl.addEventListener('blur', onBlurInput);
    }
    return () => {
      if (inputEl && onBlurInput) {
        /**
         * 안드로이드 구글 키보드 사용 환경에서 해당 이벤트 사용시 X버튼이 눌리지 않는 버그가 있습니다.
         * 해결책은 NavibarInputField의 blur 를 지연시켜서 처리하도록 했는데 타이밍 이슈로 인해 재현될 수 있습니다.
         */
        inputEl.removeEventListener('blur', onBlurInput);
      }
    };
  }, [onBlurInput]);

  const mobileHeaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mobileHeaderEl = mobileHeaderRef.current;
    const inputEl = inputRef.current;
    if (!mobileHeaderEl || !inputEl) {
      return;
    }

    function blurInput() {
      if (!mobileHeaderEl || !inputEl) {
        return;
      }
      if (document.activeElement === inputEl) {
        inputEl.blur();
      }
    }
    window.addEventListener('touchstart', blurInput);
    mobileHeaderEl.addEventListener('touchstart', stopPropagationEvent);
    return () => {
      window.removeEventListener('touchstart', blurInput);
      mobileHeaderEl.removeEventListener('touchstart', stopPropagationEvent);
    };
  }, []);

  return (
    <div
      ref={mobileHeaderRef}
      className="flex w-full items-center bg-theme-bg-base px-18pxr"
      style={{
        height: MOBILE_HEADER_HEIGHT_PX,
      }}
      {...putTiaraObjectProps({
        click: {
          layer1: '상단',
        },
        customProps: autoCompleteCustomProps,
      })}
    >
      <div className="shrink-0">
        <Icon onClick={onClickBack} type={IconType.NavigationBack} />
      </div>
      <div className="ml-10pxr flex flex-1">
        <div
          className="flex flex-1"
          {...putTiaraObjectProps({
            click: {
              layer1: '상단',
              copy: searchText,
            },
            search: {
              search_term: searchText,
              search_type: '입력검색어',
            },
          })}
        >
          <NavibarInputField
            value={searchText}
            onChange={onChangeSearchText}
            placeholder={searchPlaceholder}
            onClickRemoveButton={event => {
              LOG_CLICK_EVENT('검색어삭제_클릭', event, () =>
                onClickClearSearchText?.(event)
              );
            }}
            maxLength={SEARCH_INPUT_MAX_LENGTH}
            onSubmit={event => {
              aggregateAndSendLog({
                targetElem: event.target as HTMLElement,
                actionType: 'Event',
                actionName: '입력검색어_실행',
                actionKind: 'Search',
                click: {
                  layer1: '키패드',
                },
                customProps: {
                  user_action: 'click',
                },
              });
              onSubmit?.(event);
            }}
            onClick={event =>
              LOG_CLICK_EVENT('검색박스_클릭', event, () =>
                onClickSearchInput?.(event)
              )
            }
            isMobile={isMobile}
            ref={inputRef}
          />
        </div>
        {shouldShowCancelButton && (
          <button
            className="ml-10pxr shrink-0"
            onClick={e =>
              LOG_CLICK_EVENT('취소_클릭', e, () => onClickCancelButton?.(e))
            }
            {...putTiaraObjectProps({
              click: {
                layer1: '상단',
              },
            })}
          >
            <Text className="font-small1 text-theme-solid-60">
              {t('common_cancel_button')}
            </Text>
          </button>
        )}
      </div>
    </div>
  );
}
